import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import { saveAs } from 'file-saver';
import { v4 as uuid } from 'uuid';
import moment from 'moment';
import { t } from 'i18next';
import { Tooltip } from 'react-tooltip';
import { useSelector } from 'react-redux';
// import { useLocation } from 'react-router-dom';

import Button from 'shared/components/form/button';
import { ForeCastType, ScaleType } from 'shared/interface/enums';
import HttpService from 'shared/services/http.service';
import authService from 'shared/services/auth.service';
import CustomModal from 'shared/components/modal/modal';
import { API_CONFIG, courseGroupMapper, frequencyHierarchy, FrequencyMapper } from 'shared/constants/constants';
import { Translate } from 'shared/util/translation/translate';
import { notify } from 'shared/components/notification/notification';
import localizationConstants from 'shared/util/translation/constants';
import {
	calculateInicatorAverages,
	exchangeRate,
	getChartDataBasedOnGroupBy,
	getDataTypeDisplayName,
	getStartDateForExport,
	replaceHashtagFromUrl
} from 'shared/util/utility';
import { getChartDataBasedOnIndicators, getHighestPriorityFrequency, getIndicatorsWindowSize } from 'features/futuretable/util/chartUtility';
import { IIndicatorConfig } from './stockChart';
import { IChartData, IChartDetails, IChartSettingsType, ICompareChartIndicators, ISearchMaterialData } from '../interface/futureTable';
import { QuestionMarkIcon } from 'shared/icons/icon';
import { State } from 'shared/interface';

interface IProps {
	setIsExcelDownloadOpen: (action: boolean) => void;
	setAction: (action: string) => void;
	id: string;
	chartType: string;
	span: number | string;
	chartDetails: IChartDetails;
	symbol: string;
	customChartSpan: {
		von: string;
		bis: string;
	};
	comparativeChartIndicator: ICompareChartIndicators[] | ISearchMaterialData[] | IChartSettingsType[];
	chartSettings: IChartSettingsType[];
	scaleType: string;
	chartPeriod: string;
	forecastKeys: string[];
	chartFrequency: string[];
	chartData: IChartData[];
	selectedDataTypesPerMaterial: { [materialKey: string]: string[] };
	selectedFrequencies: { [materialKey: string]: string };
	allChartDetails: {
		[key: string]: IChartDetails;
	};
	currencyType: string;
}

const AdvanceExportChartDataPopup: React.FC<IProps> = (props) => {
	const {
		customChartSpan,
		span,
		chartDetails,
		setAction,
		chartType,
		id,
		setIsExcelDownloadOpen,
		comparativeChartIndicator,
		chartSettings,
		scaleType,
		chartPeriod,
		forecastKeys,
		chartData,
		selectedDataTypesPerMaterial,
		selectedFrequencies,
		chartFrequency,
		allChartDetails,
		currencyType
	} = props;

	const isMatflixxForecastIncluded = useMemo(() => {
		if (!chartDetails?.sources?.length) return false;

		const normalizedSources = chartDetails.sources.map((source) =>
			source
				.replace(/\s*-\d+$/, '')
				.trim()
				.toLowerCase()
		);

		return (
			normalizedSources.includes(ForeCastType.MATFLIXX_FORECAST.toLowerCase()) ||
			normalizedSources.includes(ForeCastType.MATFLIXX_AI_FORECAST.toLowerCase())
		);
	}, [chartDetails?.sources]);

	// Calculate the highest frequency (lowest hierarchy value) from all materials
	const getHighestFrequency = useMemo(() => {
		// If there are multiple materials, find the highest frequency
		if (chartFrequency && chartFrequency.length > 1) {
			getHighestPriorityFrequency(chartFrequency);
		}

		// Single material - use chartDetails.notierung
		return FrequencyMapper[chartDetails.notierung] || '';
	}, [chartFrequency, chartDetails.notierung]);

	const [courseGroup, setCourseGroup] = useState(getHighestFrequency);
	const [loading, setLoading] = useState(false);
	const [submitChartConfigLoading, setSubmitChartConfigLoading] = useState(false);
	const [exportFormat, setExportFormat] = useState<string>('standard');
	const [exportSorting, setExportSorting] = useState<string>('ASC');
	const [selectedUuid, setSelectedUuid] = useState('');
	const [isProcessCompleted, setIsProcessCompleted] = useState(false);
	const [includeForecast, setIsIncludeForecast] = useState((isMatflixxForecastIncluded && 'include') || 'exclude');
	const [isCarryForward, setIsCarryForward] = useState('no');
	const [includeWeekends, setIsIncludeWeekends] = useState('include');
	const token = authService.getAccessToken();
	const { language, decimal_separator } = useSelector((state: State) => state.siteConfig);
	const [isDownloadClicked, setIsDownloadClicked] = useState(false);

	// Check if carry forward should be disabled
	// Disabled when selected export frequency is lower (higher hierarchy value) than any material's frequency
	const isCarryForwardDisabled = useMemo(() => {
		const downloadFrequencyLevel = frequencyHierarchy[courseGroup] || 0;

		// Check all materials in chartSettings
		return chartSettings.some((item, index) => {
			const materialKey = `${item.symbol}-${item.boerse}-${index}`;
			const materialFrequencyCode = allChartDetails[materialKey]?.notierung || chartDetails.notierung;
			const materialFrequency = FrequencyMapper[materialFrequencyCode] || '';
			const materialFrequencyLevel = frequencyHierarchy[materialFrequency] || 0;

			// If download frequency is lower priority (higher value) than material frequency, disable carry forward
			return downloadFrequencyLevel > materialFrequencyLevel && materialFrequencyLevel > 0;
		});
	}, [courseGroup, chartSettings, allChartDetails, chartDetails.notierung]);

	const getExportIndicatorData = useCallback(
		async (isIntraday: number, group: string) => {
			const promises = chartSettings.map(async (chart) => {
				let endDate = moment(chartDetails.kurszeit).format('yyyy-MM-DD HH:mm:ss');
				let startDate = span === 'max' ? '' : getStartDateForExport(span as string, endDate, []);
				if (customChartSpan.bis !== '' && customChartSpan.von !== '') {
					startDate = moment(customChartSpan.von).format('yyyy-MM-DD HH:mm:ss');
					endDate = moment(customChartSpan.bis).format('yyyy-MM-DD HH:mm:ss');
				}
				if (!isEmpty(chart.indicators)) {
					const payload = {
						symbol: chart.symbol,
						boerse: chart.boerse,
						waehrung: chart.currency.value,
						unit: chart.unit.value,
						year: parseInt(chart.history.value) || '',
						is_intraday: isIntraday,
						start: span === 'max' || chartType === 'bar' ? '' : moment(startDate).format('yyyy-MM-DD HH:mm:ss'),
						end: endDate
					};
					try {
						const response = await HttpService.get(API_CONFIG.path.getChartData, payload);
						const indicatorsFunctionNames = [] as IIndicatorConfig[];
						if (chart.indicators.length > 0) {
							chart.indicators.map((indi) => {
								indicatorsFunctionNames.push({
									indicatorKeyName: `${indi.value}-${chart.symbol}-${chart.boerse}-${chart?.id || ''}`,
									keyName: scaleType === ScaleType.PERCENTAGE_CHANGE ? 'percentage' : 'letzter',
									windowSize: getIndicatorsWindowSize(indi),
									value: indi.value
								});
							});
						}
						const chartDataBasedOnGroupBy = getChartDataBasedOnGroupBy(group, calculateInicatorAverages([...response.kurshistorie]));
						const calculatedData = getChartDataBasedOnIndicators(chartDataBasedOnGroupBy, indicatorsFunctionNames, scaleType);
						const newArray = calculatedData
							.filter((obj) => {
								const timestampRegex = /\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/;
								if (isIntraday === 1) {
									return timestampRegex.test(obj['datum']);
								} else {
									return !timestampRegex.test(obj['datum']);
								}
							})
							.map(
								// eslint-disable-next-line @typescript-eslint/no-unused-vars
								({ brief, erster, geld, hoch, letzter, letzter_, oi, percentage, settlement, tief, umsatz, count, ...rest }) => rest
							);
						return {
							key: `${chart.symbol}-${chart.boerse}-${chart.id}`,
							value: newArray
						};
					} catch (error) {
						console.error(error);
						return null;
					}
				} else {
					return {
						key: `${chart.symbol}-${chart.boerse}-${chart.id}`,
						value: []
					};
				}
			});
			const results = await Promise.all(promises);
			const newChartDataObject = results.reduce((acc: any, result) => {
				if (result) {
					acc[result.key] = result.value;
				}
				return acc;
			}, {});

			return newChartDataObject;
		},
		[chartSettings, scaleType, chartDetails.kurszeit, customChartSpan.bis, customChartSpan.von, span]
	);

	const deleteTempUuid = useCallback(() => {
		HttpService.deleteRequest(`${API_CONFIG.path.getChartMasterData}/${selectedUuid}`).then().catch();
	}, [selectedUuid]);

	const getAvailableForecastTypes = () => {
		const available = new Set<string>();
		chartData.forEach((item) => {
			const chartItem = item[`cv${chartDetails.symbol}-${chartDetails.boerse}-0`];
			if (chartItem && typeof chartItem === 'object' && 'source_data' in chartItem && chartItem.source_data) {
				const forecastData = chartItem.source_data;
				Object.keys(forecastData).forEach((key) => {
					if (forecastData[key] && Object.keys(forecastData[key]).length > 0) {
						available.add(key);
					}
				});
			}
		});
		return available;
	};

	const availableForecastTypes = getAvailableForecastTypes();

	const availableForecastsInChartData = useMemo(() => {
		const forecasts = new Set<string>();

		chartData?.forEach((dataPoint) => {
			Object.values(dataPoint).forEach((val) => {
				if (typeof val === 'object' && val !== null && val.type === ForeCastType.FORECASTED_PRICE && val.source_data) {
					Object.keys(val.source_data).forEach((sourceKey) => {
						forecasts.add(sourceKey);
					});
				}
			});
		});

		return forecasts;
	}, [chartData]);

	const visibleForecastSources = useMemo(() => {
		const allSources = chartDetails?.sources || [];
		return allSources.filter((item) => {
			return (
				availableForecastsInChartData.has(item) || forecastKeys.includes(item) || (chartType === 'bar' && availableForecastTypes.has(item))
			);
		});
	}, [chartDetails?.sources, availableForecastsInChartData, forecastKeys, chartType, availableForecastTypes]);

	const shouldShowForecastOptions = useMemo(() => {
		if (chartType === 'bar') {
			if (isEmpty(availableForecastTypes)) return false;
			const hasMatchingForecasts = forecastKeys.some((forecastKey) => availableForecastTypes.has(forecastKey));
			return hasMatchingForecasts;
		} else {
			if (isEmpty(availableForecastsInChartData)) return true;
			const hasMatchingForecasts = forecastKeys.some((forecastKey) => availableForecastsInChartData.has(forecastKey));
			return hasMatchingForecasts;
		}
	}, [availableForecastsInChartData, availableForecastTypes, forecastKeys, chartType]);

	const forCastInclude = useMemo(() => {
		return chartType === 'bar' ? shouldShowForecastOptions : !shouldShowForecastOptions;
	}, [chartType, shouldShowForecastOptions]);

	const actualTypeData = chartData.filter((item) => {
		const chartDataKey = `cv${chartDetails.symbol}-${chartDetails.boerse}-0`;
		const chartItem = item[chartDataKey];
		return chartItem && typeof chartItem === 'object' && chartItem?.type === ForeCastType.ACTUAL_PRICE;
	});
	const chartDataEndDate = actualTypeData[actualTypeData.length - 1].date;

	const advanceExportCourseHistory = useCallback(
		(courseGroup: string, indicatorData: any, chartImageConfig: any) => {
			let endDate = moment(chartDataEndDate).format('yyyy-MM-DD HH:mm:ss');
			let startDate = span === 'max' || chartType === 'bar' ? '' : getStartDateForExport(span as string, endDate, []);
			if (customChartSpan.bis !== '' && customChartSpan.von !== '') {
				startDate = moment(customChartSpan.von).format('yyyy-MM-DD HH:mm:ss');
				endDate = moment(customChartSpan.bis).format('yyyy-MM-DD HH:mm:ss');
			}

			const downloadFrequencyLevel = frequencyHierarchy[courseGroup] || 0;

			const data = chartSettings.map((item: IChartSettingsType, index) => {
				// Get the frequency for this specific material
				const materialKey = `${item.symbol}-${item.boerse}-${index}`;
				const materialFrequencyCode = allChartDetails[materialKey].notierung || chartDetails.notierung;
				const materialFrequency = FrequencyMapper[materialFrequencyCode] || '';
				const materialFrequencyLevel = frequencyHierarchy[materialFrequency] || 0;

				// Calculate isLower for this specific material
				// IsLower should be true only when material frequency is lower priority (higher hierarchy value) than download frequency
				const isLower = downloadFrequencyLevel < materialFrequencyLevel && materialFrequencyLevel > 0;

				// Calculate IsGroupBy for this specific material based on the new rules
				let isGroupBy: string;

				if (courseGroup === 'allData') {
					// If download frequency is allData, always use allData
					isGroupBy = 'allData';
				} else if (chartSettings.length > 1) {
					// Multi-material case: Special logic
					// Check if any material in the chart has the same frequency as download frequency
					const anyMaterialMatchesDownload = chartSettings.some((setting, idx) => {
						const key = `${setting.symbol}-${setting.boerse}-${idx}`;
						const freqCode = allChartDetails[key]?.notierung || chartDetails.notierung;
						const freq = FrequencyMapper[freqCode] || '';
						const freqLevel = frequencyHierarchy[freq] || 0;
						return freqLevel === downloadFrequencyLevel;
					});

					if (anyMaterialMatchesDownload) {
						// If download frequency matches ANY material's frequency
						// Then all materials with matching frequency get allData
						// And materials with higher frequency (lower hierarchy) get the download frequency
						if (materialFrequencyLevel === downloadFrequencyLevel) {
							isGroupBy = 'allData';
						} else if (materialFrequencyLevel > downloadFrequencyLevel) {
							// Material has higher frequency than download frequency
							isGroupBy = courseGroup;
						} else {
							// Material has lower frequency than download frequency
							isGroupBy = 'allData';
						}
					} else {
						// No material matches download frequency
						// Apply standard single-material logic
						if (materialFrequencyLevel < downloadFrequencyLevel) {
							isGroupBy = courseGroup;
						} else {
							isGroupBy = 'allData';
						}
					}
				} else {
					// Single material case
					if (materialFrequencyLevel === downloadFrequencyLevel) {
						// Rule: If material frequency equals download frequency, use allData
						isGroupBy = 'allData';
					} else if (materialFrequencyLevel < downloadFrequencyLevel || materialFrequencyLevel > downloadFrequencyLevel) {
						// Material frequency is higher priority (lower hierarchy value) than download frequency
						// This means we're downloading at a lower frequency than the material's native frequency
						// Use the selected download frequency for grouping
						isGroupBy = courseGroup;
					} else {
						// Material frequency is lower priority (higher hierarchy value) than download frequency
						// This means we're downloading at a higher frequency than the material's native frequency
						// Use allData
						isGroupBy = 'allData';
					}
				}

				return {
					symbol: item.symbol,
					boerse: item.boerse,
					basis_symbol: item.basis_symbol,
					chart: {
						dpi: 1,
						height: 506,
						width: 1141
					},
					indicatorData: indicatorData[`${item.symbol}-${item.boerse}-${item.id}`],
					indicatorValues: item.indicators,
					currency: item.currency.value,
					unit: item.unit.value,
					historyYear: item.history.value,
					fields: item.fields?.map((field) => getDataTypeDisplayName(field)),
					IsLower: isLower,
					IsGroupBy: isGroupBy,
					color: item.color.replace('#', '')
				};
			});
			const params = {
				data: {
					chartData: data,
					chartImageConfig: chartImageConfig,
					format: 'xlsx',
					groupby: courseGroup,
					view_start: startDate !== '' ? moment(startDate).format('yyyy-MM-DD HH:mm:ss') : startDate,
					view_end: endDate,
					order: exportSorting,
					isHistoryEnabled: !isEmpty(chartSettings.filter((item) => item.history.value !== '')),
					isPercentageEnabled: scaleType === ScaleType.PERCENTAGE_CHANGE,
					isForecastEnabled: visibleForecastSources && isMatflixxForecastIncluded && forCastInclude ? includeForecast === 'include' : false,
					isCarryForward: !isCarryForwardDisabled && isCarryForward === 'yes',
					isWeekendInclude: isCarryForward === 'yes' && courseGroup === 'daily' ? includeWeekends === 'include' : true,
					exchangeRate: exchangeRate(currencyType, t),
					currencyType: currencyType,
					originalNotierung: `${t(localizationConstants[courseGroup])}`,
					route: `excel/export?boerse=${replaceHashtagFromUrl(chartDetails.boerse)}&basis_symbol=${chartDetails.basis_symbol}&symbol=${
						chartDetails.symbol
					}&chartType=${chartType}&scaleType=${scaleType}&timeSpan=${chartPeriod}&token=${token}&id=${selectedUuid}&language=${language}${
						forecastKeys && forecastKeys.length > 0 ? `&forecastKey=${forecastKeys[0]}` : ''
					}&decimalSeparator=${decimal_separator}&includeForecast=${
						chartDetails.forecastAccess === 1 && visibleForecastSources && isMatflixxForecastIncluded && forCastInclude
							? includeForecast === 'include'
							: false
					}`
				}
			};
			HttpService.put(`${API_CONFIG.path.exportCourseHistoryNew}/${selectedUuid}`, params, {}, { responseType: 'arraybuffer' })
				.then((response) => {
					const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
					saveAs(blob, `${response.headers['content-disposition'].split('=')[1]}`);
					setAction('');
					deleteTempUuid();
					setIsExcelDownloadOpen(false);
					setLoading(false);
				})
				.catch((err) => {
					setLoading(false);
					setIsExcelDownloadOpen(false);
					notify('error', err);
					console.error(err);
				});
		},
		[
			chartDataEndDate,
			span,
			chartType,
			customChartSpan.bis,
			customChartSpan.von,
			chartSettings,
			exportSorting,
			scaleType,
			visibleForecastSources,
			isMatflixxForecastIncluded,
			forCastInclude,
			includeForecast,
			isCarryForward,
			includeWeekends,
			chartDetails.boerse,
			chartDetails.basis_symbol,
			chartDetails.symbol,
			chartDetails.forecastAccess,
			chartDetails.notierung,
			chartPeriod,
			token,
			selectedUuid,
			language,
			forecastKeys,
			decimal_separator,
			allChartDetails,
			setAction,
			deleteTempUuid,
			setIsExcelDownloadOpen,
			isCarryForwardDisabled,
			currencyType
		]
	);

	const handleImages = useCallback(
		async (group: string) => {
			const chartDiv = document.getElementById(id);
			if (chartDiv) {
				const indicatorData = await getExportIndicatorData(
					group === 'intraday' ? 1 : 0,
					group === 'allData' || group === 'intraday' ? 'daily' : group
				);
				advanceExportCourseHistory(group, indicatorData, {
					height: chartDiv?.offsetHeight,
					width: 1142
				});
			}
		},
		[advanceExportCourseHistory, getExportIndicatorData, id]
	);

	const getIsProcessCompletedFlag = useCallback(
		(id: string) => {
			HttpService.get(`${API_CONFIG.path.getChartMasterData}/${id}`)
				.then((response: any) => {
					if (!response.isCompleted && exportFormat === 'advanced') {
						setTimeout(() => {
							getIsProcessCompletedFlag(id);
						}, 2000);
					} else {
						setIsProcessCompleted(response.isCompleted);
					}
				})
				.catch((err) => {
					console.error(err);
				});
		},
		[exportFormat]
	);

	const handleAdvanceExport = useCallback((group: string) => {
		setCourseGroup(group);
		setLoading(true);
	}, []);

	const standardExportCourseHistory = async (courseGroup: string, id: string, scaleType: string) => {
		setCourseGroup(courseGroup);

		let endDate = moment(chartDataEndDate).format('yyyy-MM-DD HH:mm:ss');
		let startDate = span === 'max' || chartType === 'bar' ? '' : getStartDateForExport(span as string, endDate, []);
		if (customChartSpan.bis !== '' && customChartSpan.von !== '') {
			startDate = moment(customChartSpan.von).format('yyyy-MM-DD HH:mm:ss');
			endDate = moment(customChartSpan.bis).format('yyyy-MM-DD HH:mm:ss');
		}

		const downloadFrequencyLevel = frequencyHierarchy[courseGroup] || 0;
		const compare_instruments = comparativeChartIndicator.map((ele, index) => {
			// Get the frequency for this specific material
			const materialKey = `${ele.symbol}-${ele.boerse}-${index + 1}`;
			const materialFrequencyCode = allChartDetails[materialKey].notierung || chartDetails.notierung;
			const materialFrequency = FrequencyMapper[materialFrequencyCode] || '';
			const materialFrequencyLevel = frequencyHierarchy[materialFrequency] || 0;

			// Calculate isLower for this specific material
			// IsLower should be true only when material frequency is lower priority (higher hierarchy value) than download frequency
			const isLower = downloadFrequencyLevel < materialFrequencyLevel && materialFrequencyLevel > 0;

			// Calculate IsGroupBy for this specific material based on the new rules
			let isGroupBy: string;

			if (courseGroup === 'allData') {
				// If download frequency is allData, always use allData
				isGroupBy = 'allData';
			} else if (chartSettings.length > 1) {
				// Multi-material case: Special logic
				// Check if any material in the chart has the same frequency as download frequency
				const anyMaterialMatchesDownload = chartSettings.some((setting, idx) => {
					const key = `${setting.symbol}-${setting.boerse}-${idx}`;
					const freqCode = allChartDetails[key]?.notierung || chartDetails.notierung;
					const freq = FrequencyMapper[freqCode] || '';
					const freqLevel = frequencyHierarchy[freq] || 0;
					return freqLevel === downloadFrequencyLevel;
				});

				if (anyMaterialMatchesDownload) {
					// If download frequency matches ANY material's frequency
					// Then all materials with matching frequency get allData
					// And materials with higher frequency (lower hierarchy) get the download frequency
					if (materialFrequencyLevel === downloadFrequencyLevel) {
						isGroupBy = 'allData';
					} else if (materialFrequencyLevel > downloadFrequencyLevel) {
						// Material has higher frequency than download frequency
						isGroupBy = courseGroup;
					} else {
						// Material has lower frequency than download frequency
						isGroupBy = 'allData';
					}
				} else {
					// No material matches download frequency
					// Apply standard single-material logic
					if (materialFrequencyLevel < downloadFrequencyLevel) {
						isGroupBy = courseGroup;
					} else {
						isGroupBy = 'allData';
					}
				}
			} else {
				// Single material case
				if (materialFrequencyLevel === downloadFrequencyLevel) {
					// Rule: If material frequency equals download frequency, use allData
					isGroupBy = 'allData';
				} else if (materialFrequencyLevel < downloadFrequencyLevel || materialFrequencyLevel > downloadFrequencyLevel) {
					// Material frequency is higher priority (lower hierarchy value) than download frequency
					// This means we're downloading at a lower frequency than the material's native frequency
					// Use the selected download frequency for grouping
					isGroupBy = courseGroup;
				} else {
					// Material frequency is lower priority (higher hierarchy value) than download frequency
					// This means we're downloading at a higher frequency than the material's native frequency
					// Use allData
					isGroupBy = 'allData';
				}
			}

			return {
				symbol: ele.symbol,
				boerse: ele.boerse,
				waehrung: (ele as IChartSettingsType).currency.value,
				unit: (ele as IChartSettingsType).unit.value,
				option: {
					label: ele.name,
					label_suffix: `[${(ele as ICompareChartIndicators).currency || (ele as ISearchMaterialData).waehrung} / ${
						(ele as ICompareChartIndicators).suffix || (ele as ISearchMaterialData).einheit
					}]`,
					color: ele.color
				},
				fields: ele.fields?.map((field) => getDataTypeDisplayName(field)),
				IsLower: isLower,
				IsGroupBy: isGroupBy,
				color: ele.color?.replace('#', '') || ''
			};
		});
		const materialFrequency = FrequencyMapper[chartDetails.notierung] || '';
		const materialFrequencyLevel = frequencyHierarchy[materialFrequency] || 0;

		const params = {
			symbol: chartDetails.symbol,
			boerse: chartDetails.boerse,
			format: 'xlsx',
			groupby: materialFrequency === courseGroup || courseGroup === 'allData' ? 'allData' : courseGroup,
			order: exportSorting,
			chartmeta: {
				compare_instruments,
				view_start: startDate,
				view_end: endDate
			},
			chartImageConfig: {
				width: 1142
			},
			fields: chartSettings[0].fields?.map((field) => getDataTypeDisplayName(field)) || [],
			color: chartSettings[0].color?.replace('#', '') || '',
			IsGroupBy: materialFrequency === courseGroup || courseGroup === 'allData' ? 'allData' : courseGroup,
			isForecastEnabled: visibleForecastSources && isMatflixxForecastIncluded && forCastInclude ? includeForecast === 'include' : false,
			isCarryForward: !isCarryForwardDisabled && isCarryForward === 'yes',
			isWeekendInclude: isCarryForward === 'yes' && courseGroup === 'daily' ? includeWeekends === 'include' : true,
			IsLower: downloadFrequencyLevel < materialFrequencyLevel && materialFrequencyLevel > 0,
			waehrung: chartDetails.waehrung,
			unit: chartDetails.einheit,
			exchangeRate: exchangeRate(currencyType, t),
			currencyType: currencyType,
			originalNotierung: `${t(localizationConstants[courseGroup])}`,
			route: `excel/export?boerse=${replaceHashtagFromUrl(chartDetails.boerse)}&basis_symbol=${chartDetails.basis_symbol}&symbol=${
				chartDetails.symbol
			}&chartType=${chartType}&scaleType=${scaleType}&timeSpan=${chartPeriod}&token=${token}&id=${id}&language=${language}${
				forecastKeys && forecastKeys.length > 0 ? `&forecastKey=${forecastKeys[0]}` : ''
			}&decimalSeparator=${decimal_separator}&includeForecast=${
				chartDetails.forecastAccess === 1 && visibleForecastSources && isMatflixxForecastIncluded && forCastInclude
					? includeForecast === 'include'
					: false
			}`
		};
		setLoading(true);
		HttpService.post(`${API_CONFIG.path.exportStandardExcel}`, params, undefined, { responseType: 'arraybuffer' })
			.then((response) => {
				const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
				saveAs(blob, `${response.headers['content-disposition'].split('=')[1]}`);
				setAction('');
				setIsExcelDownloadOpen(false);
			})
			.catch((err) => {
				console.error(err);
			});
	};

	const renderExportButtons = (textKey: string, group: string) => {
		return (
			<span className='position--relative'>
				<Button btnType={`${courseGroup === group ? 'accent' : 'primary'}`} className='button-size' onClick={() => setCourseGroup(group)}>
					<Translate text={localizationConstants[textKey]} />
				</Button>
				<QuestionMarkIcon className='position--absolute bottom-30px' id={`${textKey}Info`} />
				<Tooltip
					anchorId={`${textKey}Info`}
					place='top'
					className='no-padding duplicate-chart-tooltip'
					content={`${t(localizationConstants[`${textKey}Info`])}`}
				/>
			</span>
		);
	};

	const submitChartConfig = (group = '') => {
		setSubmitChartConfigLoading(true);
		const id = uuid();
		setSelectedUuid(id);
		const params = {
			id: id,
			chartConfig: {
				...{ chartSettings, scaleType },
				span: span,
				customChartSpan: customChartSpan,
				forecastKeys: forecastKeys,
				chartType: chartType,
				selectedDataTypesPerMaterial: selectedDataTypesPerMaterial,
				selectedFrequencies: selectedFrequencies,
				courseGroup: courseGroupMapper[courseGroup]
			}
		};
		HttpService.post(`${API_CONFIG.path.addAdvanceChartConfig} `, params)
			.then(() => {
				getIsProcessCompletedFlag(id);
				setSubmitChartConfigLoading(false);
				if (group !== '') {
					standardExportCourseHistory(group, id, params?.chartConfig?.scaleType);
				}
			})
			.catch((err) => {
				console.error(err);
				setSubmitChartConfigLoading(false);
			});
	};

	useEffect(() => {
		if (isProcessCompleted) {
			handleImages(courseGroup);
		}
	}, [courseGroup, isProcessCompleted]);

	useEffect(() => {
		if (isDownloadClicked && exportFormat === 'advanced' && selectedUuid === '') {
			submitChartConfig();
			setIsDownloadClicked(false);
		}
	}, [exportFormat, isDownloadClicked]);

	return (
		<CustomModal
			show={true}
			className={chartDetails.isIntraday ? `download-excel-popup-big` : `download-excel-popup`}
			handleClose={() => setAction('')}
		>
			<Fragment>
				<h2 className='modal-title'>
					<Translate text={localizationConstants.exportAsExcel} />
				</h2>
				<h2 className='font--16px'>
					<Translate text={localizationConstants.exportFormat} />
				</h2>
				<div className='d-flex align-items-center mt-20 mb-16 gap--10'>
					<div className='radio-input-wrapper'>
						<input
							type='radio'
							id='standard'
							name={'format'}
							checked={exportFormat === 'standard'}
							value={'standard'}
							onChange={({ target: { value } }) => setExportFormat(value)}
						/>
						<label
							className={`pointer mr-26 text--secondary ${exportFormat === 'standard' ? 'checked' : ''}
							font--12px line-height--17px font-weight--400 text--dark-blue`}
							htmlFor={'standard'}
						>
							<span className='position--relative'>
								<Translate text={localizationConstants.standard} />
								<QuestionMarkIcon className='position--absolute ml-5' id={'standardInfo'} />
								<Tooltip
									anchorId={'standardInfo'}
									place='top'
									className='no-padding duplicate-chart-tooltip'
									content={`${t(localizationConstants.standardInfo)}`}
								/>
							</span>
						</label>
					</div>
					<div className='radio-input-wrapper'>
						<input
							type='radio'
							id='advanced'
							name={'format'}
							checked={exportFormat === 'advanced'}
							value={'advanced'}
							onChange={({ target: { value } }) => setExportFormat(value)}
						/>
						<label
							className={`pointer mr-26 text--secondary ${exportFormat === 'advanced' ? 'checked' : ''}
							font--12px line-height--17px font-weight--400 text--dark-blue`}
							htmlFor={'advanced'}
						>
							<span className='position--relative'>
								<Translate text={localizationConstants.advanced} />
								<QuestionMarkIcon className='position--absolute ml-5' id={'advancedInfo'} />
								<Tooltip
									anchorId={'advancedInfo'}
									place='top'
									className='no-padding duplicate-chart-tooltip'
									content={`${t(localizationConstants.advancedInfo)}`}
								/>
							</span>
						</label>
					</div>
				</div>
				<h2 className='font--16px mb-16'>
					<Translate text={localizationConstants.sorting} />
				</h2>
				<div className='d-flex align-items-center mb-16 gap--10'>
					<div className='radio-input-wrapper cursor-pointer'>
						<input
							type='radio'
							id='ASC'
							name={'sorting'}
							checked={exportSorting === 'ASC'}
							value={'ASC'}
							onChange={({ target: { value } }) => setExportSorting(value)}
						/>
						<label
							className={`pointer mr-26 text--secondary ${exportSorting === 'ASC' ? 'checked' : ''}
						font--12px line-height--17px font-weight--400 text--dark-blue`}
							htmlFor={'ASC'}
						>
							<span className='position--relative'>
								<Translate text={localizationConstants.ascending} />
								<QuestionMarkIcon className='position--absolute ml-5' id={'ascendingInfo'} />
								<Tooltip
									anchorId={'ascendingInfo'}
									place='top'
									className='no-padding duplicate-chart-tooltip'
									content={`${t(localizationConstants.ascendingInfo)}`}
								/>
							</span>
						</label>
					</div>
					<div className='radio-input-wrapper'>
						<input
							type='radio'
							id='DESC'
							name={'sorting'}
							checked={exportSorting === 'DESC'}
							value={'DESC'}
							onChange={({ target: { value } }) => setExportSorting(value)}
						/>
						<label
							className={`pointer mr-26 text--secondary ${exportSorting === 'DESC' ? 'checked' : ''}
							font--12px line-height--17px font-weight--400 text--dark-blue`}
							htmlFor={'DESC'}
						>
							<span className='position--relative'>
								<Translate text={localizationConstants.descending} />
								<QuestionMarkIcon className='position--absolute ml-5' id={'descendingInfo'} />
								<Tooltip
									anchorId={'descendingInfo'}
									place='top'
									className='no-padding duplicate-chart-tooltip'
									content={`${t(localizationConstants.descendingInfo)}`}
								/>
							</span>
						</label>
					</div>
				</div>
				<h2 className='font--16px mb-16'>
					<Translate text={localizationConstants.grouping} />
				</h2>
				<div className='full-width text-align-right d-flex justify-content-start align-items-center gap--10'>
					{chartDetails.isIntraday && renderExportButtons('intraday', 'intraday')}
					{renderExportButtons('daily', 'daily')}
					{renderExportButtons('weekly', 'weekly')}
					{renderExportButtons('monthly', 'monthly')}
					{chartDetails.notierung === 'Q' && renderExportButtons('quaterly', 'quaterly')}
					{chartDetails.notierung === '2' && renderExportButtons('biMonthly', 'biMonthly')}
					{chartDetails.notierung === 'B' && renderExportButtons('biWeekly', 'biWeekly')}
					{chartDetails.notierung === 'C' && renderExportButtons('continually', 'continually')}
					{chartDetails.notierung === 'H' && renderExportButtons('halfYearly', 'halfYearly')}
					{chartDetails.notierung === 'I' && renderExportButtons('irregular', 'irregular')}
				</div>

				<h2 className='font--16px mb-16'>
					<Translate text={localizationConstants.carryForward} />
				</h2>
				<div className='d-flex align-items-center mb-16 gap--10'>
					<div className='radio-input-wrapper cursor-pointer'>
						<input
							type='radio'
							id='yes'
							name={'carryForward'}
							checked={isCarryForward === 'yes'}
							value={'yes'}
							onChange={({ target: { value } }) => setIsCarryForward(value)}
							disabled={isCarryForwardDisabled}
						/>
						<label
							className={`pointer mr-26 text--secondary ${isCarryForward === 'yes' ? 'checked' : ''}
						font--12px line-height--17px font-weight--400 text--dark-blue ${isCarryForwardDisabled ? 'opacity-5' : ''}`}
							htmlFor={'yes'}
						>
							<span className='position--relative'>
								<Translate text={localizationConstants.yes} />
								<QuestionMarkIcon className='position--absolute ml-5' id={'carryForwardYesInfo'} />
								<Tooltip
									anchorId={'carryForwardYesInfo'}
									place='top'
									className='no-padding duplicate-chart-tooltip'
									content={`${t(localizationConstants.carryForwardYesInfo)}`}
								/>
							</span>
						</label>
					</div>
					<div className='radio-input-wrapper'>
						<input
							type='radio'
							id='no'
							name={'carryForward'}
							checked={isCarryForward === 'no'}
							value={'no'}
							onChange={({ target: { value } }) => setIsCarryForward(value)}
							disabled={isCarryForwardDisabled}
						/>
						<label
							className={`pointer mr-26 text--secondary ${isCarryForward === 'no' ? 'checked' : ''}
							font--12px line-height--17px font-weight--400 text--dark-blue ${isCarryForwardDisabled ? 'opacity-5' : ''}`}
							htmlFor={'no'}
						>
							<span className='position--relative'>
								<Translate text={localizationConstants.no} />
								<QuestionMarkIcon className='position--absolute ml-5' id={'carryForwardNoInfo'} />
								<Tooltip
									anchorId={'carryForwardNoInfo'}
									place='top'
									className='no-padding duplicate-chart-tooltip'
									content={`${t(localizationConstants.carryForwardNoInfo)}`}
								/>
							</span>
						</label>
					</div>
				</div>
				{isCarryForward === 'yes' && courseGroup === 'daily' && (
					<>
						<h2 className='font--16px mb-16'>
							<Translate text={localizationConstants.includeWeekends} />
						</h2>
						<div className='d-flex align-items-center mb-16 gap--10'>
							<div className='radio-input-wrapper cursor-pointer'>
								<input
									type='radio'
									id='include-weekend'
									name={'include-weekend'}
									checked={includeWeekends === 'include'}
									value={'include'}
									onChange={({ target: { value } }) => setIsIncludeWeekends(value)}
								/>
								<label
									className={`pointer mr-26 text--secondary ${includeWeekends === 'include' ? 'checked' : ''}
						font--12px line-height--17px font-weight--400 text--dark-blue`}
									htmlFor={'include-weekend'}
								>
									<span className='position--relative'>
										<Translate text={localizationConstants.include} />
										<QuestionMarkIcon className='position--absolute ml-5' id={'include-weekendInfo'} />
										<Tooltip
											anchorId={'include-weekendInfo'}
											place='top'
											className='no-padding duplicate-chart-tooltip'
											content={`${t(localizationConstants.includesWeekendYesInfo)}`}
										/>
									</span>
								</label>
							</div>
							<div className='radio-input-wrapper'>
								<input
									type='radio'
									id='exclude-weekend'
									name={'include-weekend'}
									checked={includeWeekends === 'exclude'}
									value={'exclude'}
									onChange={({ target: { value } }) => setIsIncludeWeekends(value)}
								/>
								<label
									className={`pointer mr-26 text--secondary ${includeWeekends === 'exclude' ? 'checked' : ''}
							font--12px line-height--17px font-weight--400 text--dark-blue`}
									htmlFor={'exclude-weekend'}
								>
									<span className='position--relative'>
										<Translate text={localizationConstants.exclude} />
										<QuestionMarkIcon className='position--absolute ml-5' id={'exclude-weekendInfo'} />
										<Tooltip
											anchorId={'exclude-weekendInfo'}
											place='top'
											className='no-padding duplicate-chart-tooltip'
											content={`${t(localizationConstants.includesWeekendNoInfo)}`}
										/>
									</span>
								</label>
							</div>
						</div>
					</>
				)}

				{chartDetails.forecastAccess === 1 && visibleForecastSources && isMatflixxForecastIncluded && forCastInclude && (
					<>
						<h2 className='font--16px mb-16'>
							<Translate text={localizationConstants.includeForecast} />
						</h2>
						<div className='d-flex align-items-center mb-16 gap--10'>
							<div className='radio-input-wrapper cursor-pointer'>
								<input
									type='radio'
									id='include-forecast'
									name={'include-forecast'}
									checked={includeForecast === 'include'}
									value={'include'}
									onChange={({ target: { value } }) => setIsIncludeForecast(value)}
									disabled={!isMatflixxForecastIncluded}
								/>
								<label
									className={`pointer mr-26 text--secondary ${includeForecast === 'include' ? 'checked' : ''}
						font--12px line-height--17px font-weight--400 text--dark-blue`}
									htmlFor={'include-forecast'}
								>
									<span className='position--relative'>
										<Translate text={localizationConstants.include} />
										<QuestionMarkIcon className='position--absolute ml-5' id={'include-forecastInfo'} />
										<Tooltip
											anchorId={'include-forecastInfo'}
											place='top'
											className='no-padding duplicate-chart-tooltip'
											content={`${t(localizationConstants.includesForecastYesInfo)}`}
										/>
									</span>
								</label>
							</div>
							<div className='radio-input-wrapper'>
								<input
									type='radio'
									id='exclude-forecast'
									name={'include-forecast'}
									checked={includeForecast === 'exclude'}
									value={'exclude'}
									onChange={({ target: { value } }) => setIsIncludeForecast(value)}
									disabled={!isMatflixxForecastIncluded}
								/>
								<label
									className={`pointer mr-26 text--secondary ${includeForecast === 'exclude' ? 'checked' : ''}
							font--12px line-height--17px font-weight--400 text--dark-blue`}
									htmlFor={'exclude-forecast'}
								>
									<span className='position--relative'>
										<Translate text={localizationConstants.exclude} />
										<QuestionMarkIcon className='position--absolute ml-5' id={'exclude-forecastInfo'} />
										<Tooltip
											anchorId={'exclude-forecastInfo'}
											place='top'
											className='no-padding duplicate-chart-tooltip'
											content={`${t(localizationConstants.includesForecastNoInfo)}`}
										/>
									</span>
								</label>
							</div>
						</div>
					</>
				)}
				<div className='full-width text-align-right mt-10 d-flex justify-content-start align-items-center gap--10'>
					<Button
						btnType='primary'
						className='full-width'
						onClick={() => {
							if (exportFormat === 'advanced') {
								setIsDownloadClicked(true);
								handleAdvanceExport(courseGroup);
							} else {
								submitChartConfig(courseGroup);
							}
						}}
						loading={loading}
						disabled={submitChartConfigLoading}
					>
						<Translate text={localizationConstants.download} />
					</Button>
				</div>

				{loading && exportFormat === 'advanced' && (
					<div className='position--absolute chart-iframe'>
						<div className='postion--relative'>
							<div className='overlay z-index--1 display-flex-center mt-0 full-height position--absolute'>
								<h2 className='color-white'>
									<Translate text={localizationConstants.generatingChartImages} />
								</h2>
							</div>
							<iframe
								src={`${process.env.REACT_APP_EXCEL_EXPORT_URL}/${token}/${selectedUuid}/${language}/${decimal_separator}/${
									chartDetails.forecastAccess === 1 && visibleForecastSources && isMatflixxForecastIncluded && forCastInclude
										? includeForecast === 'include'
										: false
								}`}
								height={500}
								width={1141}
							/>
						</div>
					</div>
				)}
			</Fragment>
		</CustomModal>
	);
};

export default AdvanceExportChartDataPopup;
